import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import { DOCUMENT } from '../../constants/documentTheme';
import { partnerDangerLinkSx, partnerLinkSx, partnerOutlineBtnSx, partnerOxbloodBtnSx } from '../PartnerListChrome';
import { formatMoney } from '../../utils/partnerFinance';
import { CHARGE_TEMPLATES, downloadBlob } from '../../utils/groupBilling';
import {
  BillingStatusChip,
  OutlineChip,
  SectionCard,
  SummaryTile,
  eyebrowSx,
  fieldSx,
  useConfirm,
} from './BillingParts';
import GroupChargeDialog from './GroupChargeDialog';
import GroupSendInvoiceDialog from './GroupSendInvoiceDialog';
import GroupRecordPaymentDialog from './GroupRecordPaymentDialog';
import GroupBillingSettingsDialog from './GroupBillingSettingsDialog';
import GroupAccountDialog from './GroupAccountDialog';
import GroupRoomsBoard, { GroupUnitDialog } from './GroupRoomsBoard';

const partnerAuth = { usePartnerAuth: true };

function chargeWhoLabel(charge, t) {
  const n = charge.allocations.length;
  if (!n) return t('groupBilling.charges.nobody');
  if (n <= 2) return charge.allocations.map((a) => a.travelerName).join(', ');
  return t('groupBilling.charges.travelerCount', { count: n });
}

const PartnerGroupBilling = ({ masterTripId, billing, loading = false, error = '', onBillingChange, onReload, agencyPanel = null }) => {
  const { t } = useTranslation();
  const [confirm, confirmElement] = useConfirm();
  const [notice, setNotice] = useState('');
  const [actionError, setActionError] = useState('');
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const [templateAnchor, setTemplateAnchor] = useState(null);
  const [chargeDialog, setChargeDialog] = useState(null);
  const [invoiceDialog, setInvoiceDialog] = useState(null);
  const [paymentDialog, setPaymentDialog] = useState(null);
  const [accountDialogId, setAccountDialogId] = useState(null);
  const [unitDialog, setUnitDialog] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [newPayer, setNewPayer] = useState(null);
  const [cancelCharge, setCancelCharge] = useState(null);
  const [supplierCharge, setSupplierCharge] = useState(null);

  const base = `/partners/trips/${masterTripId}/billing`;
  const currency = billing?.currency || 'USD';

  /** Runs a billing request and swaps in the fresh overview. Returns { data } or { error }. */
  const call = useCallback(async (method, path, body, okMessage) => {
    setActionError('');
    try {
      const response = method === 'delete' || method === 'get'
        ? await api[method](`${base}${path}`, partnerAuth)
        : await api[method](`${base}${path}`, body || {}, partnerAuth);
      if (response.data?.billing) onBillingChange(response.data.billing);
      if (okMessage) setNotice(okMessage);
      return { data: response.data };
    } catch (err) {
      const message = err.response?.data?.error || t('groupBilling.errors.generic');
      return { error: message };
    }
  }, [base, onBillingChange, t]);

  const runDialog = async (method, path, body, okMessage, close) => {
    setSaving(true);
    setDialogError('');
    const res = await call(method, path, body, okMessage);
    setSaving(false);
    if (res.error) setDialogError(res.error);
    else close();
    return res;
  };

  const runInline = async (method, path, body, okMessage) => {
    const res = await call(method, path, body, okMessage);
    if (res.error) setActionError(res.error);
    return res;
  };

  const downloadPdf = async (invoice) => {
    try {
      const { data } = await api.get(`${base}/invoices/${invoice.id}/pdf`, { ...partnerAuth, responseType: 'blob' });
      downloadBlob(data, `${invoice.number}.pdf`, 'application/pdf');
    } catch {
      setActionError(t('groupBilling.errors.pdf'));
    }
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get(`${base}/export.csv`, { ...partnerAuth, responseType: 'blob' });
      downloadBlob(data, `group-billing-${masterTripId}.csv`, 'text/csv');
    } catch {
      setActionError(t('groupBilling.errors.export'));
    }
  };

  const copyPayLink = async (account) => {
    const lead = billing.travelers.find((tr) => tr.accountId === account.id && tr.isLead)
      || billing.travelers.find((tr) => tr.accountId === account.id);
    if (!lead) return;
    const url = `${window.location.origin}/trips/${lead.id}?tab=billing`;
    try {
      await navigator.clipboard.writeText(url);
      setNotice(t('groupBilling.notices.linkCopied'));
    } catch {
      setNotice(url);
    }
  };

  const accountForDialog = useMemo(
    () => billing?.accounts.find((a) => a.id === accountDialogId) || null,
    [billing, accountDialogId]
  );
  const invoiceAccount = billing?.accounts.find((a) => a.id === invoiceDialog?.accountId) || null;
  const paymentAccount = billing?.accounts.find((a) => a.id === paymentDialog?.accountId) || null;

  if (loading && !billing) {
    return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>;
  }
  if (!billing) {
    return (
      <Alert severity="error" action={<Button onClick={onReload}>{t('groupBilling.retry')}</Button>}>
        {error || t('groupBilling.errors.load')}
      </Alert>
    );
  }

  const { summary } = billing;
  const openCharges = billing.charges.filter((c) => c.status !== 'void');
  const voidCharges = billing.charges.filter((c) => c.status === 'void');

  const addChargeButton = (
    <Button
      startIcon={<AddIcon />}
      sx={partnerOxbloodBtnSx}
      onClick={(e) => setTemplateAnchor(e.currentTarget)}
      data-testid="group-billing-add-charge"
    >
      {t('groupBilling.charges.add')}
    </Button>
  );

  return (
    <Stack spacing={3} data-testid="partner-group-billing">
      {confirmElement}
      {actionError ? <Alert severity="error" onClose={() => setActionError('')}>{actionError}</Alert> : null}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 26 }}>{t('groupBilling.title')}</Typography>
          <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink3, maxWidth: 640 }}>{t('groupBilling.subtitle')}</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button sx={partnerOutlineBtnSx} onClick={() => { setDialogError(''); setSettingsOpen(true); }} data-testid="group-billing-settings">
            {t('groupBilling.paymentSettings')}
          </Button>
          <Button sx={partnerOutlineBtnSx} onClick={exportCsv} disabled={!billing.active} data-testid="group-billing-export">
            {t('groupBilling.export')}
          </Button>
        </Stack>
      </Box>

      <Box
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: '1px', bgcolor: DOCUMENT.ruleHard, border: `1px solid ${DOCUMENT.ruleHard}` }}
        data-testid="group-billing-summary"
      >
        <SummaryTile label={t('groupBilling.summary.sold')} value={formatMoney(summary.sold, currency, { showCode: false })}
          sub={summary.unallocated ? t('groupBilling.summary.unallocated', { amount: formatMoney(summary.unallocated, currency) }) : null}
          testId="group-summary-sold" />
        <SummaryTile label={t('groupBilling.summary.collected')} value={formatMoney(summary.collected, currency, { showCode: false })} testId="group-summary-collected" />
        <SummaryTile label={t('groupBilling.summary.outstanding')} value={formatMoney(summary.outstanding, currency, { showCode: false })} testId="group-summary-outstanding" />
        <SummaryTile
          label={t('groupBilling.summary.nextDue')}
          value={summary.nextDue ? formatMoney(summary.nextDue.amount, currency, { showCode: false }) : '—'}
          sub={summary.nextDue ? `${summary.nextDue.date} · ${summary.nextDue.accountName}` : null}
          testId="group-summary-next"
        />
        <SummaryTile
          label={t('groupBilling.summary.commission')}
          value={formatMoney(summary.commissionExpected, currency, { showCode: false })}
          sub={summary.supplierOwed ? t('groupBilling.summary.supplierOwed', { amount: formatMoney(summary.supplierOwed, currency) }) : null}
          testId="group-summary-commission"
        />
      </Box>

      {!billing.active && (
        <Alert severity="info" data-testid="group-billing-intro">{t('groupBilling.intro')}</Alert>
      )}

      <SectionCard title={t('groupBilling.charges.title')} action={addChargeButton} testId="group-billing-charges">
        {!openCharges.length && (
          <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink3 }}>{t('groupBilling.charges.empty')}</Typography>
        )}
        {openCharges.map((charge) => (
          <Box
            key={charge.id}
            data-testid={`group-charge-row-${charge.id}`}
            sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2.4fr 1.6fr 1fr auto' }, gap: 1.5, py: 1.25, borderTop: `1px solid ${DOCUMENT.rule}`, alignItems: 'center' }}
          >
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 14.5 }}>{charge.name}</Typography>
              <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3 }}>
                {t(`groupBilling.category.${charge.category}`)} · {t(`groupBilling.basis.${charge.unitBasis}`)}
                {charge.sourceBookingId ? ` · ${t('groupBilling.charges.fromBooking')}` : ''}
                {charge.dueInFull ? ` · ${t('groupBilling.charges.dueNow')}` : ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <OutlineChip label={t(`groupBilling.split.${charge.splitMethod}`)} />
              <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2 }}>{chargeWhoLabel(charge, t)}</Typography>
              {charge.unallocated ? <OutlineChip label={t('groupBilling.charges.unallocated')} /> : null}
            </Box>
            <Box sx={{ textAlign: { md: 'right' } }}>
              <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18 }}>{formatMoney(charge.total, currency)}</Typography>
              {charge.commission ? (
                <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3 }}>
                  {t('groupBilling.charges.commission', { amount: formatMoney(charge.commission, currency) })}
                </Typography>
              ) : null}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: { md: 'flex-end' } }}>
              <Button size="small" sx={partnerLinkSx} data-testid={`group-charge-edit-${charge.id}`}
                onClick={() => { setDialogError(''); setChargeDialog({ charge }); }}>
                {t('common.edit')}
              </Button>
              {charge.cost != null && (
                <Button size="small" sx={partnerLinkSx} onClick={() => setSupplierCharge({ charge, paid: String(charge.supplierPaid || 0), due: charge.supplierDueDate || '' })}>
                  {t('groupBilling.charges.supplier')}
                </Button>
              )}
              <Button size="small" sx={partnerDangerLinkSx} data-testid={`group-charge-cancel-${charge.id}`}
                onClick={() => setCancelCharge({ charge, waivePenalty: false })}>
                {t('groupBilling.charges.cancel')}
              </Button>
            </Box>
          </Box>
        ))}
        {voidCharges.length > 0 && (
          <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mt: 1.5 }}>
            {t('groupBilling.charges.cancelledCount', { count: voidCharges.length })}
          </Typography>
        )}
      </SectionCard>

      <GroupRoomsBoard
        units={billing.units}
        travelers={billing.travelers}
        charges={billing.charges}
        onAdd={() => { setDialogError(''); setUnitDialog({ unit: null }); }}
        onEdit={(unit) => { setDialogError(''); setUnitDialog({ unit }); }}
        onDelete={async (unit) => {
          if (await confirm(t('groupBilling.rooms.deleteConfirm', { label: unit.label }))) {
            runInline('delete', `/units/${unit.id}`, undefined, t('groupBilling.notices.deleted'));
          }
        }}
        onSetOccupants={async (unitId, ids) => {
          const res = await runInline('put', `/units/${unitId}/occupants`, { occupants: ids });
          return !res.error;
        }}
      />

      <SectionCard
        title={t('groupBilling.accounts.title')}
        testId="group-billing-accounts"
        action={(
          <Button sx={partnerOutlineBtnSx} onClick={() => { setDialogError(''); setNewPayer({ name: '', email: '' }); }} data-testid="group-billing-add-payer">
            {t('groupBilling.accounts.add')}
          </Button>
        )}
      >
        <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mb: 1.5 }}>{t('groupBilling.accounts.hint')}</Typography>
        {billing.accounts.map((account) => {
          const names = billing.travelers.filter((tr) => tr.accountId === account.id).map((tr) => tr.name);
          const eff = account.effectiveCollection || {};
          return (
            <Box
              key={account.id}
              data-testid={`group-account-row-${account.id}`}
              sx={{ borderTop: `1px solid ${DOCUMENT.rule}`, py: 1.5 }}
            >
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1.2fr 1.2fr' }, gap: 1.5, alignItems: 'center' }}>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: 15 }}>{account.name}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3 }}>
                    {names.length ? t('groupBilling.accounts.pays', { names: names.join(', ') }) : t('groupBilling.account.noTravelers')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <BillingStatusChip status={account.status} testId={`group-account-status-${account.id}`} />
                  <OutlineChip
                    label={t('groupBilling.accounts.collectVia', { method: t(`groupBilling.collection.${eff.method || 'in_app'}`) })}
                    testId={`group-account-collect-${account.id}`}
                  />
                  {account.clientReported && <OutlineChip label={t('groupBilling.account.clientReported')} />}
                </Box>
                <Box sx={{ textAlign: { md: 'right' } }}>
                  <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18 }}>
                    {formatMoney(account.balance, currency)}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3 }}>
                    {t('groupBilling.accounts.ofTotal', { paid: formatMoney(account.paid, currency), total: formatMoney(account.total, currency) })}
                    {account.nextDue ? ` · ${t('groupBilling.accounts.nextDue', { date: account.nextDue.date })}` : ''}
                  </Typography>
                </Box>
              </Box>
              {account.outOfDate && (
                <Alert
                  severity="warning"
                  sx={{ mt: 1 }}
                  data-testid={`group-account-outdated-${account.id}`}
                  action={(
                    <Stack direction="row" spacing={1}>
                      <Button size="small" sx={partnerLinkSx} data-testid={`group-account-adjust-${account.id}`}
                        onClick={() => setInvoiceDialog({ accountId: account.id, mode: 'adjustment' })}>
                        {account.invoiceDelta < 0 ? t('groupBilling.accounts.sendCredit') : t('groupBilling.accounts.sendExtra')}
                      </Button>
                      <Button size="small" sx={partnerLinkSx}
                        onClick={() => {
                          const live = account.invoices.find((i) => i.status !== 'void' && i.type === 'invoice');
                          if (live) setInvoiceDialog({ accountId: account.id, mode: 'reissue', invoiceId: live.id });
                        }}>
                        {t('groupBilling.accounts.reissue')}
                      </Button>
                    </Stack>
                  )}
                >
                  {t('groupBilling.accounts.outOfDate', { amount: formatMoney(Math.abs(account.invoiceDelta), currency) })}
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 1 }}>
                {!account.hasInvoice && account.total > 0 && (
                  <Button size="small" sx={partnerOxbloodBtnSx} data-testid={`group-account-send-${account.id}`}
                    onClick={() => setInvoiceDialog({ accountId: account.id, mode: 'issue' })}>
                    {t('groupBilling.accounts.sendInvoice')}
                  </Button>
                )}
                {account.hasInvoice && (
                  <Button size="small" sx={partnerLinkSx} onClick={() => copyPayLink(account)} data-testid={`group-account-copy-${account.id}`}>
                    {t('groupBilling.accounts.copyLink')}
                  </Button>
                )}
                {account.total > 0 && (
                  <Button size="small" sx={partnerLinkSx} data-testid={`group-account-pay-${account.id}`}
                    onClick={() => { setDialogError(''); setPaymentDialog({ accountId: account.id }); }}>
                    {t('groupBilling.accounts.recordPayment')}
                  </Button>
                )}
                <Button size="small" sx={partnerLinkSx} data-testid={`group-account-open-${account.id}`}
                  onClick={() => setAccountDialogId(account.id)}>
                  {t('groupBilling.accounts.details')}
                </Button>
              </Box>
            </Box>
          );
        })}
      </SectionCard>

      {agencyPanel ? (
        <Box>
          <Typography sx={{ ...eyebrowSx, mb: 1 }}>{t('groupBilling.agencyEarnings')}</Typography>
          {agencyPanel}
        </Box>
      ) : null}

      <Menu anchorEl={templateAnchor} open={Boolean(templateAnchor)} onClose={() => setTemplateAnchor(null)}>
        {Object.keys(CHARGE_TEMPLATES).map((key) => (
          <MenuItem
            key={key}
            data-testid={`group-charge-template-${key}`}
            onClick={() => {
              setTemplateAnchor(null);
              setDialogError('');
              setChargeDialog({ charge: null, template: key });
            }}
          >
            {t(`groupBilling.templates.${key}`)}
          </MenuItem>
        ))}
      </Menu>

      <GroupChargeDialog
        open={Boolean(chargeDialog)}
        onClose={() => setChargeDialog(null)}
        charge={chargeDialog?.charge || null}
        template={chargeDialog?.template || 'custom'}
        travelers={billing.travelers}
        units={billing.units}
        currency={currency}
        saving={saving}
        error={dialogError}
        onSave={(payload) => {
          const editing = chargeDialog?.charge;
          runDialog(
            editing ? 'put' : 'post',
            editing ? `/charges/${editing.id}` : '/charges',
            payload,
            t('groupBilling.notices.chargeSaved'),
            () => setChargeDialog(null)
          );
        }}
      />

      <GroupSendInvoiceDialog
        open={Boolean(invoiceDialog)}
        onClose={() => setInvoiceDialog(null)}
        masterTripId={masterTripId}
        account={invoiceAccount}
        invoiceId={invoiceDialog?.invoiceId}
        mode={invoiceDialog?.mode || 'issue'}
        settings={billing.settings}
        currency={currency}
        onDone={(data) => {
          if (data?.billing) onBillingChange(data.billing);
          setInvoiceDialog(null);
          setNotice(data?.email?.sent === false
            ? t('groupBilling.notices.issuedNoEmail', { number: data.number })
            : t('groupBilling.notices.issued', { number: data?.number || '' }));
        }}
      />

      <GroupRecordPaymentDialog
        open={Boolean(paymentDialog)}
        onClose={() => setPaymentDialog(null)}
        account={paymentAccount}
        currency={currency}
        saving={saving}
        error={dialogError}
        onSave={(body) => runDialog(
          'post',
          `/accounts/${paymentDialog.accountId}/payments`,
          { ...body, installmentId: body.installmentId || paymentDialog.installmentId || null },
          t('groupBilling.notices.paymentRecorded'),
          () => setPaymentDialog(null)
        )}
      />

      <GroupAccountDialog
        open={Boolean(accountForDialog)}
        onClose={() => setAccountDialogId(null)}
        account={accountForDialog}
        accounts={billing.accounts}
        travelers={billing.travelers}
        currency={currency}
        call={call}
        confirm={confirm}
        onDownloadPdf={downloadPdf}
        onSendInvoice={(mode, invoiceId) => setInvoiceDialog({ accountId: accountDialogId, mode, invoiceId })}
        onRecordPayment={(installmentId) => {
          setDialogError('');
          setPaymentDialog({ accountId: accountDialogId, installmentId });
        }}
      />

      <GroupUnitDialog
        open={Boolean(unitDialog)}
        onClose={() => setUnitDialog(null)}
        unit={unitDialog?.unit || null}
        saving={saving}
        error={dialogError}
        onSave={(body) => runDialog(
          unitDialog?.unit ? 'put' : 'post',
          unitDialog?.unit ? `/units/${unitDialog.unit.id}` : '/units',
          body,
          t('groupBilling.notices.saved'),
          () => setUnitDialog(null)
        )}
      />

      <GroupBillingSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={billing.settings}
        schedule={billing.schedule}
        currency={currency}
        saving={saving}
        error={dialogError}
        onSave={async ({ settings, schedule }) => {
          setSaving(true);
          setDialogError('');
          const first = await call('put', '/settings', settings);
          const second = first.error ? first : await call('put', '/schedule', schedule);
          setSaving(false);
          if (second.error) setDialogError(second.error);
          else {
            setSettingsOpen(false);
            setNotice(t('groupBilling.notices.saved'));
          }
        }}
      />

      <Dialog open={Boolean(newPayer)} onClose={() => setNewPayer(null)} maxWidth="xs" fullWidth data-testid="group-new-payer-dialog">
        <DialogTitle sx={{ fontFamily: DOCUMENT.display }}>{t('groupBilling.accounts.add')}</DialogTitle>
        <DialogContent>
          {dialogError ? <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert> : null}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField size="small" sx={fieldSx} label={t('groupBilling.account.name')} value={newPayer?.name || ''}
              onChange={(e) => setNewPayer((p) => ({ ...p, name: e.target.value }))}
              helperText={t('groupBilling.accounts.nameHint')} inputProps={{ 'data-testid': 'group-new-payer-name' }} />
            <TextField size="small" sx={fieldSx} label={t('groupBilling.account.email')} value={newPayer?.email || ''}
              onChange={(e) => setNewPayer((p) => ({ ...p, email: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button sx={partnerLinkSx} onClick={() => setNewPayer(null)}>{t('common.cancel')}</Button>
          <Button
            sx={partnerOxbloodBtnSx}
            disabled={saving || !newPayer?.name?.trim()}
            data-testid="group-new-payer-save"
            onClick={() => runDialog('post', '/accounts', { name: newPayer.name, email: newPayer.email || null },
              t('groupBilling.notices.saved'), () => setNewPayer(null))}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(cancelCharge)} onClose={() => setCancelCharge(null)} maxWidth="xs" fullWidth data-testid="group-cancel-charge-dialog">
        <DialogTitle sx={{ fontFamily: DOCUMENT.display }}>{t('groupBilling.charges.cancelTitle')}</DialogTitle>
        <DialogContent>
          {dialogError ? <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert> : null}
          <Typography sx={{ fontSize: 14, mb: 1 }}>
            {t('groupBilling.charges.cancelBody', { name: cancelCharge?.charge.name })}
          </Typography>
          {(cancelCharge?.charge.penaltyAmount || cancelCharge?.charge.refundable === false) && (
            <FormControlLabel
              control={(
                <Checkbox
                  checked={Boolean(cancelCharge?.waivePenalty)}
                  onChange={(e) => setCancelCharge((c) => ({ ...c, waivePenalty: e.target.checked }))}
                />
              )}
              label={t('groupBilling.charges.waivePenalty')}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button sx={partnerLinkSx} onClick={() => setCancelCharge(null)}>{t('common.cancel')}</Button>
          <Button
            sx={partnerLinkSx}
            data-testid="group-charge-delete"
            onClick={() => runDialog('delete', `/charges/${cancelCharge.charge.id}`, undefined,
              t('groupBilling.notices.deleted'), () => setCancelCharge(null))}
          >
            {t('groupBilling.charges.deleteInstead')}
          </Button>
          <Button
            sx={partnerOxbloodBtnSx}
            disabled={saving}
            data-testid="group-charge-cancel-confirm"
            onClick={() => runDialog('post', `/charges/${cancelCharge.charge.id}/cancel`, { waivePenalty: cancelCharge.waivePenalty },
              t('groupBilling.notices.chargeCancelled'), () => setCancelCharge(null))}
          >
            {t('groupBilling.charges.cancelConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(supplierCharge)} onClose={() => setSupplierCharge(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: DOCUMENT.display }}>{t('groupBilling.charges.supplierTitle', { name: supplierCharge?.charge.name })}</DialogTitle>
        <DialogContent>
          {dialogError ? <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert> : null}
          <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mb: 2 }}>
            {t('groupBilling.charges.supplierCost', { amount: formatMoney(supplierCharge?.charge.cost, currency) })}
          </Typography>
          <Stack spacing={2}>
            <TextField size="small" sx={fieldSx} type="number" label={t('groupBilling.charges.supplierPaid')}
              value={supplierCharge?.paid || ''} onChange={(e) => setSupplierCharge((s) => ({ ...s, paid: e.target.value }))} />
            <TextField size="small" sx={fieldSx} type="date" label={t('groupBilling.charges.supplierDue')} InputLabelProps={{ shrink: true }}
              value={supplierCharge?.due || ''} onChange={(e) => setSupplierCharge((s) => ({ ...s, due: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button sx={partnerLinkSx} onClick={() => setSupplierCharge(null)}>{t('common.cancel')}</Button>
          <Button
            sx={partnerOxbloodBtnSx}
            disabled={saving}
            onClick={() => runDialog('put', `/charges/${supplierCharge.charge.id}/supplier`,
              { supplierPaid: Number(supplierCharge.paid) || 0, supplierDueDate: supplierCharge.due || null },
              t('groupBilling.notices.saved'), () => setSupplierCharge(null))}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={4000}
        onClose={() => setNotice('')}
        message={notice}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Stack>
  );
};

export default PartnerGroupBilling;
